import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import TierSelector from "./TierSelector";
import { CLUMSE_SEARCH_FEE_USD } from "../utils/tierClassification";

/**
 * Compact payment CTA for chat dispatch. Matches Clumse action styling (orange).
 * @param {"comfortable" | "compact"} density - comfortable: main rail; compact: narrow sidebar
 * @param {"full" | "short" | "none"} trustVariant - trust line under the button
 * @param {boolean} blueprintMode - when true, shifts copy from "Find Pros" to "Generate Blueprint Pack"
 */
const PaymentSecurityGroup = ({
  onDispatch,
  disabled = false,
  loading = false,
  dispatchFee: _dispatchFee = CLUMSE_SEARCH_FEE_USD,
  included = false,
  className = "",
  initialTier = "MINOR",
  autoDetectedTier = null,
  showTierSelector = true,
  density = "comfortable",
  trustVariant = "full",
  blueprintMode = false,
}) => {
  const [selectedTier, setSelectedTier] = useState(
    autoDetectedTier || initialTier
  );

  useEffect(() => {
    if (autoDetectedTier) setSelectedTier(autoDetectedTier);
  }, [autoDetectedTier]);

  const syncedFee = Number(_dispatchFee);
  const feeNum =
    included || !Number.isFinite(syncedFee) || syncedFee <= 0
      ? CLUMSE_SEARCH_FEE_USD
      : syncedFee;

  let buttonMainText;
  let buttonSubText;
  if (included) {
    buttonMainText = blueprintMode
      ? "Generate Blueprint Pack"
      : "Start vendor search";
    buttonSubText = "(included in your plan)";
  } else if (blueprintMode) {
    buttonMainText = `Get My Blueprint Pack · $${feeNum}`;
    buttonSubText = null;
  } else {
    buttonMainText = `Pay $${feeNum} · Start search`;
    buttonSubText = null;
  }

  const handleDispatch = () => {
    onDispatch(selectedTier);
  };

  const isCompact = density === "compact";
  const showTrust = !included && trustVariant !== "none";
  const trustLabel =
    trustVariant === "short" || isCompact
      ? blueprintMode
        ? "Third-party scope doc · Stripe-secured"
        : "Stripe-secured"
      : null;

  return (
    <SecurityGroupContainer className={className} $density={density}>
      {showTierSelector && !included && (
        <TierSelector
          selectedTier={selectedTier}
          onTierSelect={setSelectedTier}
          autoSelected={!!autoDetectedTier}
        />
      )}

      <DispatchButton
        type="button"
        onClick={handleDispatch}
        disabled={disabled || loading}
        $loading={loading}
        $compact={isCompact}
      >
        <ButtonMainText>{buttonMainText}</ButtonMainText>
        {buttonSubText && (
          <ButtonSubText $compact={isCompact}>{buttonSubText}</ButtonSubText>
        )}
      </DispatchButton>

      {showTrust && (
        <TrustMicro $compact={isCompact || trustVariant === "short"}>
          {trustLabel ? (
            <>
              <SafetyCertificateOutlined aria-hidden />
              {trustLabel}
            </>
          ) : (
            "One-time fee · Stripe-secured"
          )}
        </TrustMicro>
      )}
    </SecurityGroupContainer>
  );
};

const SecurityGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$density === "compact" ? "6px" : "6px")};
  padding: ${(p) => (p.$density === "compact" ? "0" : "0")};
  background: transparent;
  border: none;
  border-radius: 0;
`;

const DispatchButton = styled.button`
  width: 100%;
  box-sizing: border-box;
  padding: ${(p) => (p.$compact ? "10px 14px" : "12px 16px")};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${(p) => (p.$compact ? "2px" : "3px")};
  text-transform: none;
  color: #fff;
  background: ${(p) =>
    p.$loading
      ? "#9aa0a6"
      : "linear-gradient(180deg, #ff7043 0%, var(--color-action, #ff5722) 48%, #e64a19 100%)"};
  border: none;
  border-radius: ${(p) => (p.$compact ? "12px" : "12px")};
  cursor: pointer;
  box-shadow: ${(p) =>
    p.$compact
      ? "0 2px 8px rgba(255, 87, 34, 0.28), 0 1px 2px rgba(60, 64, 67, 0.12)"
      : "0 2px 10px rgba(255, 87, 34, 0.32), 0 1px 3px rgba(60, 64, 67, 0.15)"};
  transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;
  font-family: var(--font-stack-sans, "Inter", system-ui, sans-serif);

  &:hover:not(:disabled) {
    background: ${(p) =>
      p.$loading
        ? "#9aa0a6"
        : "linear-gradient(180deg, #ff8a65 0%, #ff5722 45%, #d84315 100%)"};
    box-shadow: ${(p) =>
      p.$compact
        ? "0 4px 14px rgba(255, 87, 34, 0.38)"
        : "0 4px 16px rgba(255, 87, 34, 0.4)"};
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
    box-shadow: 0 1px 4px rgba(255, 87, 34, 0.25);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const ButtonMainText = styled.span`
  font-size: ${(p) => (p.$compact ? "0.875rem" : "0.9rem")};
  font-weight: 650;
  letter-spacing: 0.02em;
  line-height: 1.2;
`;

const ButtonSubText = styled.span`
  font-size: ${(p) => (p.$compact ? "0.7rem" : "0.75rem")};
  font-weight: 500;
  opacity: 0.9;
  line-height: 1.2;
`;

const TrustMicro = styled.p`
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  text-align: center;
  font-size: ${(p) => (p.$compact ? "0.65rem" : "0.68rem")};
  line-height: 1.35;
  color: #80868b;

  .anticon {
    font-size: 0.72rem;
    color: #9aa0a6;
  }
`;

export default PaymentSecurityGroup;
